/**
 * Rocks Types
 *
 * TypeScript interfaces for the ATEK Rocks feature (EOS-style quarterly rocks).
 */

// =============================================================================
// Enums and Constants
// =============================================================================

export type RockStatus = 'on_track' | 'off_track' | 'complete' | 'canceled' | 'draft';
export type RockType = 'company' | 'individual';
export type BoardColumn = 'current' | 'next' | 'later' | 'future' | 'long_term_issues';

export const ROCK_STATUSES: { value: RockStatus; label: string }[] = [
  { value: 'on_track', label: 'On Track' },
  { value: 'off_track', label: 'Off Track' },
  { value: 'complete', label: 'Complete' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'draft', label: 'Draft' },
];

export const BOARD_COLUMNS: { value: BoardColumn; label: string }[] = [
  { value: 'current', label: 'Current' },
  { value: 'next', label: 'Next' },
  { value: 'later', label: 'Later' },
  { value: 'future', label: 'Future' },
  { value: 'long_term_issues', label: 'Long-Term Issues' },
];

// =============================================================================
// Core Types
// =============================================================================

export interface Rock {
  id: string;
  title: string;
  description: string | null;
  type: RockType;
  status: RockStatus;
  ownerId: string;
  teamId: string;
  teamIds: string[];
  quarter: string;
  dueDate: string;
  boardColumn: BoardColumn;
  sortOrder: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deliverables?: Deliverable[];
}

export interface Deliverable {
  id: string;
  rockId: string;
  title: string;
  description: string | null;
  ownerId: string | null;
  dueDate: string | null;
  completed: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface Comment {
  id: string;
  entityType: string;
  entityId: string;
  authorId: string;
  body: string;
  createdAt: string;
}

export interface LinkedItem {
  id: string;
  sourceType: string;
  sourceId: string;
  targetType: string;
  targetId: string;
  createdAt: string;
}

export interface Team {
  id: string;
  name: string;
  type: string;
  isPrivate: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TeamMember {
  id: string;
  teamId: string;
  userId: string;
  role: string;
  createdAt: string;
}

// =============================================================================
// Input Types
// =============================================================================

export interface CreateRockInput {
  title: string;
  type?: RockType;
  status?: RockStatus;
  owner_id?: string;
  team_id: string;
  team_ids?: string[];
  quarter: string;
  due_date: string;
  board_column?: BoardColumn;
  description?: string;
}

export interface UpdateRockInput {
  title?: string;
  description?: string;
  type?: RockType;
  status?: RockStatus;
  owner_id?: string;
  team_id?: string;
  team_ids?: string[];
  quarter?: string;
  due_date?: string;
  board_column?: BoardColumn;
}

export interface CreateDeliverableInput {
  title: string;
  description?: string;
  owner_id?: string;
  due_date?: string;
  sort_order?: number;
}

// =============================================================================
// Mastra Team Assignments
// =============================================================================

export interface MastraTeamAssignment {
  id: string;
  entity_type: string;
  entity_id: string;
  team_id: string;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// Filter Types
// =============================================================================

export interface RockFilters {
  [key: string]: unknown;
  team_id?: string;
  owner_id?: string;
  status?: RockStatus;
  quarter?: string;
  archived?: string;
  board_column?: BoardColumn;
  search?: string;
}
